import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tag, Package, Layers, Tractor, AlertTriangle, PackageOpen, Anchor, Paperclip, FileText } from "lucide-react";
import DateTimePicker from "../components/ui/DateTimePicker";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";

const INPUT_CLASS = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-950 focus:ring-2 focus:ring-slate-950/10";

const emptyLivestockItem = () => ({
  livestockType: "cattle", animalClass: "", headCount: "", averageWeightKg: "",
  lastFed: "", lastWatered: "", fitForTransport: false, vetCertificateUploaded: false, notes: "",
});

const emptyFreightItem = () => ({
  freightType: "pallet", description: "", quantity: "", weightKg: "", cubeM3: "",
  lengthM: "", widthM: "", heightM: "", notes: "",
});

const BOOKING_TYPES = [
  { value: "livestock", label: "Livestock", Icon: Tag },
  { value: "freight", label: "Freight", Icon: Package },
  { value: "mixed", label: "Mixed", Icon: Layers },
  { value: "machinery", label: "Machinery", Icon: Tractor },
  { value: "dg", label: "Dangerous Goods", Icon: AlertTriangle },
  { value: "breakbulk", label: "Break Bulk", Icon: PackageOpen },
];

const CAPACITY_LIMITS = {
  livestockHeads: 1200,
  weightKg: 250000,
  freightCube: 300,
};

export default function NewBooking({ editBookingId = "", initialData = null, onSaved = null } = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(editBookingId);

  const [sailings, setSailings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [standardRates, setStandardRates] = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ownersRiskExecuted, setOwnersRiskExecuted] = useState(Boolean(initialData?.ownersRiskStatement?.executed));
  const [ownersRiskName, setOwnersRiskName] = useState(initialData?.ownersRiskStatement?.signedName || "");
  const [uploading, setUploading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState(() => (initialData?.documents || []).map(d => ({ ...d })));
  const [message, setMessage] = useState("");

  const [form, setForm] = useState(() => {
    const base = {
      reference: "",
      bookingType: "livestock",
      status: "pending",
      customerId: "",
      oneOffName: "",
      sailingId: searchParams.get("sailingId") || "",
      notes: "",
      // Sender ("from") — who's consigning the goods/stock
      senderName: "",
      senderCompany: "",
      senderPhone: "",
      senderEmail: "",
      senderAddress: "",
      agentName: "",
      agentPhone: "",
      agentEmail: "",
      // Receiver ("to") — who's receiving at destination
      receiverName: "",
      receiverCompany: "",
      receiverPhone: "",
      receiverEmail: "",
      deliveryAddress: "",
      // Emergency contact — captured on every portal booking request; kept
      // visible here too so editing a portal booking doesn't lose it.
      emergencyContact: "",
      emergencyPhone: "",
      vetEmergencyPhone: "",
    };
    if (!initialData) return base;
    // Map a saved booking record back into this form's shape for editing —
    // same field mapping BookingRequestPage.jsx already uses for its own
    // edit mode, kept consistent here.
    const sender = initialData.onlineRequest?.sender || {};
    const agent = initialData.onlineRequest?.agent || {};
    const receiver = initialData.onlineRequest?.receiver || {};
    const emergency = initialData.onlineRequest?.emergency || {};
    return {
      ...base,
      reference: initialData.bookingRef || "",
      bookingType: initialData.bookingType || base.bookingType,
      status: initialData.status || base.status,
      // A booking with no linked customer record is a one-off / casual customer.
      customerId:
        initialData.customer?._id || initialData.customer || initialData.customerId ||
        (initialData.customerName ? "__oneoff__" : ""),
      oneOffName: !(initialData.customer?._id || initialData.customer || initialData.customerId)
        ? initialData.customerName || ""
        : "",
      sailingId:
        initialData.sailing?._id || initialData.sailing || initialData.sailingId || base.sailingId,
      notes: initialData.notes || "",
      senderName: sender.name || "",
      senderCompany: sender.company || "",
      senderPhone: sender.phone || "",
      senderEmail: sender.email || "",
      senderAddress: sender.address || "",
      agentName: agent.name || "",
      agentPhone: agent.phone || "",
      agentEmail: agent.email || "",
      receiverName: receiver.name || "",
      receiverCompany: receiver.company || "",
      receiverPhone: receiver.phone || "",
      receiverEmail: receiver.email || "",
      deliveryAddress: receiver.address || "",
      emergencyContact: emergency.contact || "",
      emergencyPhone: emergency.phone || "",
      vetEmergencyPhone: emergency.vetPhone || "",
    };
  });

  // Livestock line items
  const [livestockItems, setLivestockItems] = useState(() =>
    initialData?.livestockItems?.length
      ? initialData.livestockItems.map(item => ({
          livestockType: item.livestockType || "cattle",
          animalClass: item.animalClass || "",
          headCount: item.headCount != null ? String(item.headCount) : "",
          averageWeightKg: item.averageWeightKg != null ? String(item.averageWeightKg) : "",
          lastFed: item.lastFed ? new Date(item.lastFed).toISOString().slice(0, 16) : "",
          lastWatered: item.lastWatered ? new Date(item.lastWatered).toISOString().slice(0, 16) : "",
          fitForTransport: Boolean(item.fitForTransport),
          vetCertificateUploaded: Boolean(item.vetCertificateUploaded),
          notes: item.notes || "",
        }))
      : [emptyLivestockItem()]
  );

  // Freight line items
  const [freightItems, setFreightItems] = useState(() =>
    initialData?.freightItems?.length
      ? initialData.freightItems.map(item => ({
          freightType: item.freightType || "pallet",
          description: item.description || "",
          quantity: item.quantity != null ? String(item.quantity) : "",
          weightKg: item.weightKg != null ? String(item.weightKg) : "",
          cubeM3: item.cubeM3 != null ? String(item.cubeM3) : "",
          lengthM: item.lengthM != null && item.lengthM !== 0 ? String(item.lengthM) : "",
          widthM: item.widthM != null && item.widthM !== 0 ? String(item.widthM) : "",
          heightM: item.heightM != null && item.heightM !== 0 ? String(item.heightM) : "",
          notes: item.notes || "",
        }))
      : [emptyFreightItem()]
  );

  // Machinery
  const [machinery, setMachinery] = useState(() => {
    const m = initialData?.machinery;
    if (!m) {
      return { description: "", make: "", model: "", weightKg: "", lengthM: "", widthM: "", heightM: "", roRo: true, hazardous: false, notes: "" };
    }
    return {
      description: m.description || "",
      make: m.make || "",
      model: m.model || "",
      weightKg: m.weightKg != null ? String(m.weightKg) : "",
      lengthM: m.lengthM != null ? String(m.lengthM) : "",
      widthM: m.widthM != null ? String(m.widthM) : "",
      heightM: m.heightM != null ? String(m.heightM) : "",
      roRo: m.roRo ?? true,
      hazardous: Boolean(m.hazardous),
      notes: m.notes || "",
    };
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);

      const [sailingsRes, customersRes, bookingsRes, ratesRes] = await Promise.all([
        fetch(`${API_BASE}/sailings`),
        fetch(`${API_BASE}/customers`),
        fetch(`${API_BASE}/bookings`),
        fetch(`${API_BASE}/rates`),
      ]);

      const sailingsData = await sailingsRes.json();
      const customersData = await customersRes.json();
      const bookingsData = await bookingsRes.json();
      const ratesData = await ratesRes.json();

      setSailings(Array.isArray(sailingsData) ? sailingsData : (sailingsData?.data || []));
      setCustomers(Array.isArray(customersData) ? customersData : (customersData?.data || []));
      setExistingBookings(Array.isArray(bookingsData) ? bookingsData : (bookingsData?.data || []));
      setStandardRates(Array.isArray(ratesData) ? ratesData : (ratesData?.data || []));
    } catch (error) {
      console.error(error);
      setMessage(`Could not load booking data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  function updateField(key, value) {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  const selectedCustomer = customers.find(
    (customer) => String(customer._id) === String(form.customerId)
  );

  const isOneOff = form.customerId === "__oneoff__";

  const selectedSailing = sailings.find(
    (sailing) => String(sailing._id) === String(form.sailingId)
  );

  // Only Open or Draft sailings can be newly picked — matches the same
  // server-side rule enforced in booking.route.js. When editing, the
  // booking's already-assigned sailing stays selectable even if it's since
  // moved on (e.g. to Loading or Departed) — otherwise the dropdown would
  // silently drop the sailing this booking is actually on.
  const bookableSailings = useMemo(() => {
    return sailings.filter((sailing) =>
      ["open", "draft"].includes(String(sailing.status || "").toLowerCase()) ||
      (isEditing && String(sailing._id) === String(form.sailingId))
    );
  }, [sailings, isEditing, form.sailingId]);

  const selectedCustomerName = isOneOff
    ? form.oneOffName.trim() || "One-off customer"
    : selectedCustomer?.companyName ||
    selectedCustomer?.tradingName ||
    selectedCustomer?.contactName ||
    selectedCustomer?.name ||
    "Unknown Customer";

  const selectedSailingLabel = selectedSailing
    ? getSailingLabel(selectedSailing)
    : "";

  const confirmedSailingBookings = useMemo(() => {
    if (!selectedSailing) return [];

    return existingBookings.filter((booking) => {
      // Editing an existing booking: it's already sitting in
      // existingBookings (just fetched fresh), so counting it here as
      // well as via the form's own live totals would double-count
      // whatever it's already contributing to the sailing.
      if (isEditing && String(booking._id) === String(editBookingId)) return false;

      const status = String(booking.status || "").toLowerCase();

      if (!["confirmed", "invoiced", "completed"].includes(status)) {
        return false;
      }

      const bookingSailingId =
        booking.sailingId?._id ||
        booking.sailingId ||
        booking.sailing?._id ||
        booking.sailing;

      const sailingRef = selectedSailing.sailingRef || selectedSailing.sailingId;

      return (
        String(bookingSailingId) === String(selectedSailing._id) ||
        String(booking.sailingId) === String(selectedSailing._id) ||
        String(booking.sailing) === String(selectedSailing._id) ||
        booking.requestedSailingRef === sailingRef ||
        booking.sailingSnapshot?.sailingMongoId === selectedSailing._id ||
        booking.sailingSnapshot?.sailingRef === sailingRef
      );
    });
  }, [existingBookings, selectedSailing]);

  const currentSailingUsage = useMemo(() => {
    let livestockHeads = 0;
    let weightKg = 0;
    let freightCube = 0;

    confirmedSailingBookings.forEach((booking) => {
      (booking.livestockItems || []).forEach((item) => {
        const heads = Number(item.headCount || 0);
        const averageWeight = Number(item.averageWeightKg || 0);

        livestockHeads += heads;
        weightKg +=
          Number(item.totalWeightKg || 0) || heads * averageWeight;
      });

      (booking.freightItems || []).forEach((item) => {
        weightKg += Number(item.weightKg || 0);
        freightCube += Number(item.cubeM3 || 0);
      });
    });

    return {
      livestockHeads,
      weightKg,
      freightCube,
    };
  }, [confirmedSailingBookings]);

  const draftBookingTotals = useMemo(() => {
    let totalHead = 0, weightKg = 0, freightCube = 0;
    if (form.bookingType === "livestock" || form.bookingType === "mixed") {
      livestockItems.forEach(item => {
        totalHead += Number(item.headCount || 0);
        weightKg += Number(item.headCount || 0) * Number(item.averageWeightKg || 0);
      });
    }
    if (form.bookingType === "freight" || form.bookingType === "mixed" || form.bookingType === "breakbulk" || form.bookingType === "dg") {
      freightItems.forEach(item => {
        weightKg += Number(item.weightKg || 0);
        freightCube += Number(item.cubeM3 || 0);
      });
    }
    if (form.bookingType === "machinery") {
      weightKg += Number(machinery.weightKg || 0);
    }
    return { livestockHeads: totalHead, weightKg, freightCube };
  }, [form.bookingType, livestockItems, freightItems, machinery]);

  const projectedUsage = useMemo(() => {
    return {
      livestockHeads:
        currentSailingUsage.livestockHeads + draftBookingTotals.livestockHeads,
      weightKg: currentSailingUsage.weightKg + draftBookingTotals.weightKg,
      freightCube: currentSailingUsage.freightCube + draftBookingTotals.freightCube,
    };
  }, [currentSailingUsage, draftBookingTotals]);

  const capacityCheck = useMemo(() => {
    if (!selectedSailing) {
      return {
        valid: true,
        message: "",
      };
    }

    if (projectedUsage.livestockHeads > CAPACITY_LIMITS.livestockHeads) {
      return {
        valid: false,
        message: "Livestock capacity exceeded for this sailing.",
      };
    }

    if (projectedUsage.weightKg > CAPACITY_LIMITS.weightKg) {
      return {
        valid: false,
        message: "Weight capacity exceeded for this sailing.",
      };
    }

    if (projectedUsage.freightCube > CAPACITY_LIMITS.freightCube) {
      return {
        valid: false,
        message: "Freight cube capacity exceeded for this sailing.",
      };
    }

    return {
      valid: true,
      message: "",
    };
  }, [projectedUsage, selectedSailing]);

  const showLivestock = ["livestock", "mixed"].includes(form.bookingType);
  const showFreight = ["freight", "mixed", "breakbulk", "dg"].includes(form.bookingType);
  const showMachinery = form.bookingType === "machinery";

  // Itemized pricing — one line per consignment/freight item, each priced
  // using its OWN type against the customer's rate card. This replaces a
  // previous version that read from form.headCount / form.livestockType /
  // form.freightQuantity etc — fields that never actually existed on `form`
  // (the real data lives in the livestockItems/freightItems arrays), so the
  // old calculation always silently evaluated to $0 regardless of what was
  // actually entered.
  // Live rate lookup — checks the customer's actual override list against the
  // CURRENT master rate, rather than reading customer.rates.livestock/freight
  // (a snapshot only refreshed when someone last saved that customer's rate
  // page). That snapshot approach had two real bugs: it could go stale if a
  // master rate changed afterward, and — in an earlier version of the
  // rate-saving logic — a livestock per-kg rate like Cattle could leak into
  // the freight.perKg slot since the old checks weren't scoped by type. This
  // mirrors invoice.route.js's corrected, type-scoped lookup exactly, so the
  // on-screen estimate here matches what "Calculate Charges" will actually
  // produce once the booking is saved.
  const customerOverrideMap = useMemo(() => {
    const map = {};
    (selectedCustomer?.rateOverrides || []).forEach((o) => {
      if (o.rate) map[String(o.rate)] = o.price;
    });
    return map;
  }, [selectedCustomer]);

  const effectiveRatePrice = (rate) => {
    if (!rate) return 0;
    const override = customerOverrideMap[String(rate._id)];
    return override != null ? Number(override) : Number(rate.price || 0);
  };

  const findRateByName = (type, names) =>
    standardRates.find((r) => r.active !== false && r.type === type && names.includes(String(r.name || "").toLowerCase().trim()));

  const findRateByUnit = (type, unit) =>
    standardRates.find((r) => r.active !== false && r.type === type && r.unit === unit);

  const pricingLines = useMemo(() => {
    if (!selectedCustomer && !isOneOff) return [];
    const lines = [];

    if (showLivestock) {
      const livestockNameMap = {
        cattle: ["cattle"],
        sheep: ["sheep"],
        deer: ["deer"],
      };
      livestockItems.forEach((item) => {
        const head = Number(item.headCount || 0);
        if (!head) return;
        const names = livestockNameMap[item.livestockType] || ["other livestock", "other"];
        const rate = findRateByName("livestock", names);
        const price = effectiveRatePrice(rate);
        lines.push({
          label: `${toTitle(item.livestockType)}${item.animalClass ? ` (${toTitle(item.animalClass)})` : ""}`,
          basis: `${head.toLocaleString()} head × $${price.toLocaleString()}`,
          amount: head * price,
        });
      });
    }

    if (showFreight) {
      const freightNameMap = {
        pallet: ["pallet"],
        container: ["container"],
        vehicle: ["vehicle"],
        looseItem: ["loose item", "loose_item"],
      };
      freightItems.forEach((item) => {
        const qty = Number(item.quantity || 0);
        const weight = Number(item.weightKg || 0);
        const cube = Number(item.cubeM3 || 0);
        const linearM = Number(item.lengthM || 0) * qty;
        const label = item.description || toTitle(item.freightType);
        const unitKey = item.freightType === "looseItem" ? "looseItem" : item.freightType;

        const qtyRate = freightNameMap[unitKey] ? findRateByName("freight", freightNameMap[unitKey]) : null;
        const perKgRate = findRateByUnit("freight", "per_kg");
        const perM3Rate = findRateByUnit("freight", "per_m3");

        if (qtyRate && qtyRate.unit === "per_lm" && linearM > 0) {
          const price = effectiveRatePrice(qtyRate);
          lines.push({ label, basis: `${linearM.toLocaleString()}m × $${price.toLocaleString()}/m`, amount: linearM * price });
        } else if (qtyRate && qtyRate.unit === "per_lm") {
          lines.push({ label, basis: "Enter length (m) to price per linear metre", amount: 0 });
        } else if (qtyRate && qty > 0) {
          const price = effectiveRatePrice(qtyRate);
          lines.push({ label, basis: `${qty.toLocaleString()} × $${price.toLocaleString()} per ${toTitle(unitKey)}`, amount: qty * price });
        } else if (weight > 0 && perKgRate) {
          const price = effectiveRatePrice(perKgRate);
          lines.push({ label, basis: `${weight.toLocaleString()}kg × $${price}/kg`, amount: weight * price });
        } else if (cube > 0 && perM3Rate) {
          const price = effectiveRatePrice(perM3Rate);
          lines.push({ label, basis: `${cube.toLocaleString()}m³ × $${price}/m³`, amount: cube * price });
        } else if (qty > 0 || weight > 0 || cube > 0) {
          // Has quantity but no matching rate configured — surface as $0
          // rather than silently omitting the line, so it's obvious a rate
          // needs setting up rather than looking "free".
          lines.push({ label, basis: "No matching rate configured", amount: 0 });
        }
      });
    }

    if (showMachinery && (machinery.description || Number(machinery.weightKg) > 0 || Number(machinery.lengthM) > 0)) {
      // Vehicles / machinery are charged per linear metre (length of the unit
      // along the deck), using the "vehicle" per-lm rate, or any per-lm
      // freight rate if there isn't one named that way.
      const lmRate =
        findRateByName("freight", ["vehicle", "vehicles", "machinery"]) ||
        standardRates.find((r) => r.active !== false && r.type === "freight" && r.unit === "per_lm");
      const linearM = Number(machinery.lengthM || 0);
      const label = machinery.description || "Machinery / Vehicle";
      if (lmRate && lmRate.unit === "per_lm" && linearM > 0) {
        const price = effectiveRatePrice(lmRate);
        lines.push({ label, basis: `${linearM.toLocaleString()}m × $${price.toLocaleString()}/m`, amount: linearM * price });
      } else if (lmRate && lmRate.unit === "per_lm") {
        lines.push({ label, basis: "Enter length (m) to price per linear metre", amount: 0 });
      } else {
        lines.push({ label, basis: "No per-linear-metre rate configured — price manually", amount: 0 });
      }
    }

    return lines;
  }, [selectedCustomer, isOneOff, customerOverrideMap, standardRates, livestockItems, freightItems, machinery, showLivestock, showFreight, showMachinery]);

  const calculatedPricing = useMemo(
    () => pricingLines.reduce((sum, line) => sum + line.amount, 0),
    [pricingLines]
  );

  const pricingBreakdown = useMemo(
    () => pricingLines.map((l) => `${l.label}: ${l.basis}`).join("; ") || "-",
    [pricingLines]
  );

  async function handleSave(e) {
    e.preventDefault();
    setMessage("");

    if (!form.customerId) {
      setMessage("Select a customer, or choose One-off customer.");
      return;
    }

    if (isOneOff && !form.oneOffName.trim()) {
      setMessage("Enter the customer's name for this one-off booking.");
      return;
    }

    if (!form.sailingId) {
      setMessage("Select a sailing.");
      return;
    }

    if (!capacityCheck.valid) {
      setMessage(capacityCheck.message);
      return;
    }

    if (form.status === "confirmed" && (!ownersRiskExecuted || !ownersRiskName.trim())) {
      setMessage("This booking cannot be set to Confirmed until the Owner's Risk Statement is captured below.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...(form.reference.trim() ? { bookingRef: form.reference.trim() } : {}),
        customer: isOneOff ? null : form.customerId,
        customerId: isOneOff ? null : form.customerId,
        customerName: selectedCustomerName,
        sailing: form.sailingId,
        sailingId: form.sailingId,
        requestedSailingRef: selectedSailing?.sailingRef || selectedSailingLabel || "",
        sailingSnapshot: buildSailingSnapshot(selectedSailing),
        bookingType: form.bookingType || "livestock",
        type: form.bookingType || "livestock",
        status: form.status || "pending",
        // Editing shouldn't relabel a Portal-sourced booking as "manual" —
        // only new bookings created here are genuinely manual.
        source: isEditing ? (initialData?.source || "manual") : "manual",
        ...(ownersRiskExecuted && ownersRiskName.trim()
          ? {
              ownersRiskStatement: {
                executed: true,
                signedName: ownersRiskName.trim(),
                executedAt: new Date().toISOString(),
                executedVia: "staff_manual",
              },
            }
          : {}),

        onlineRequest: {
          sender: {
            name: form.senderName,
            company: form.senderCompany,
            phone: form.senderPhone,
            email: form.senderEmail,
            address: form.senderAddress,
          },
          agent: {
            name: form.agentName,
            phone: form.agentPhone,
            email: form.agentEmail,
          },
          receiver: {
            name: form.receiverName,
            company: form.receiverCompany,
            phone: form.receiverPhone,
            email: form.receiverEmail,
            address: form.deliveryAddress,
          },
          emergency: {
            contact: form.emergencyContact,
            phone: form.emergencyPhone,
            vetPhone: form.vetEmergencyPhone,
          },
        },

        livestockItems: ["livestock", "mixed"].includes(form.bookingType)
          ? livestockItems.map(item => ({
              livestockType: item.livestockType || "cattle",
              animalClass: item.animalClass || "",
              headCount: Number(item.headCount || 0),
              averageWeightKg: Number(item.averageWeightKg || 0),
              totalWeightKg: Number(item.headCount || 0) * Number(item.averageWeightKg || 0),
              lastFed: item.lastFed || null,
              lastWatered: item.lastWatered || null,
              fitForTransport: Boolean(item.fitForTransport),
              vetCertificateUploaded: Boolean(item.vetCertificateUploaded),
              notes: item.notes || "",
            }))
          : [],

        freightItems: ["freight", "mixed", "breakbulk", "dg"].includes(form.bookingType)
          ? freightItems.map(item => ({
              freightType: item.freightType || "pallet",
              description: item.description || "",
              quantity: Number(item.quantity || 0),
              weightKg: Number(item.weightKg || 0),
              cubeM3: Number(item.cubeM3 || 0),
              lengthM: Number(item.lengthM || 0),
              widthM: Number(item.widthM || 0),
              heightM: Number(item.heightM || 0),
              notes: item.notes || "",
            }))
          : [],

        machinery: form.bookingType === "machinery" ? {
          description: machinery.description,
          make: machinery.make,
          model: machinery.model,
          weightKg: Number(machinery.weightKg || 0),
          lengthM: Number(machinery.lengthM || 0),
          widthM: Number(machinery.widthM || 0),
          heightM: Number(machinery.heightM || 0),
          roRo: machinery.roRo,
          hazardous: machinery.hazardous,
          notes: machinery.notes,
        } : null,

        documents: uploadedDocs.filter(d => d.url).map(d => ({
          name: d.name, url: d.url, type: d.type, category: d.category, uploadedAt: new Date(),
        })),

        capacitySnapshot: { before: currentSailingUsage, added: draftBookingTotals, projected: projectedUsage, limits: CAPACITY_LIMITS, valid: capacityCheck.valid },
        pricingSnapshot: { customerId: selectedCustomer?._id || "", customerName: selectedCustomerName, lines: pricingLines, calculation: pricingBreakdown, calculatedTotal: Number(calculatedPricing || 0) },
        totals: { estimatedRevenue: Number(calculatedPricing || 0), livestockHeads: draftBookingTotals.livestockHeads, weightKg: draftBookingTotals.weightKg, cubeM3: draftBookingTotals.freightCube },
        notes: form.notes || "",
      };

      const res = await fetch(
        isEditing ? `${API_BASE}/bookings/${editBookingId}` : `${API_BASE}/bookings`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Failed to ${isEditing ? "update" : "create"} booking`);
      }

      if (onSaved) {
        onSaved();
      } else {
        navigate(isEditing ? `/dashboard/bookings/${editBookingId}` : "/dashboard/bookings");
      }
    } catch (error) {
      console.error(error);
      setMessage(`Could not ${isEditing ? "update" : "create"} booking: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }

  function updateLivestock(idx, key, value) {
    setLivestockItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  }

  // Cube auto-fills from qty × L × W × H whenever those change and all three
  // dimensions are present; the cube field itself stays editable.
  function updateFreight(idx, key, value) {
    setFreightItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, [key]: value };
        if (["quantity", "lengthM", "widthM", "heightM"].includes(key)) {
          const l = Number(next.lengthM || 0), w = Number(next.widthM || 0), hh = Number(next.heightM || 0);
          if (l > 0 && w > 0 && hh > 0) {
            next.cubeM3 = String(Math.round(Math.max(Number(next.quantity || 0), 1) * l * w * hh * 1000) / 1000);
          }
        }
        return next;
      })
    );
  }

  async function handleFileUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("images", file);
      try {
        const res = await fetch(`${API_BASE}/upload/images`, { method: "POST", body: fd });
        if (!res.ok) throw new Error("Upload failed");
        const url = await res.json();
        const lower = file.name.toLowerCase();
        setUploadedDocs((prev) => [...prev, {
          name: file.name,
          url: typeof url === "string" ? url : "",
          type: file.type,
          category: lower.includes("vet") ? "vet_certificate" : lower.includes("msds") ? "msds" : lower.includes("nait") || lower.includes("tag") ? "nait" : "supporting_document",
          size: file.size,
        }]);
      } catch (err) { console.error("Upload error:", err); }
    }
    setUploading(false);
    e.target.value = "";
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="w-full p-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-600">Loading booking form...</p>
          </div>
        </div>
      </div>
    );
  }

  const statusStyles = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    invoiced: "bg-blue-50 text-blue-700 border-blue-200",
    completed: "bg-slate-100 text-slate-600 border-slate-200",
  };

  const cutoff = selectedSailing?.bookingCutoff;
  const pct = (value, limit) => Math.min(100, Math.round((value / limit) * 100));

  const backTo = isEditing ? `/dashboard/bookings/${editBookingId}` : "/dashboard/bookings";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="w-full p-6">
        <form onSubmit={handleSave} className="space-y-6">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold tracking-tight text-slate-950">
                  {isEditing ? "Edit Booking" : "New Booking"}
                </h1>
                {isEditing && form.reference && (
                  <span className="rounded-lg bg-slate-200 px-2.5 py-1 font-mono text-xs font-semibold text-slate-700">
                    {form.reference}
                  </span>
                )}
                <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${statusStyles[form.status] || statusStyles.pending}`}>
                  {form.status}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-500">
                {isEditing
                  ? "Update this booking using live sailing capacity and customer-specific pricing."
                  : "Create a booking using live sailing capacity and customer-specific pricing."}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate(backTo)}
                className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-slate-950 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {saving ? (isEditing ? "Saving..." : "Creating...") : (isEditing ? "Save Changes" : "Create Booking")}
              </button>
            </div>
          </div>

          {message && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">{message}</div>
          )}

          {/* Booking type */}
          <div className="grid grid-cols-2 gap-1 rounded-2xl border border-slate-200 bg-slate-100 p-1 sm:grid-cols-3 md:grid-cols-6">
            {BOOKING_TYPES.map((type) => {
              const active = form.bookingType === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => updateField("bookingType", type.value)}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                    active ? "bg-slate-950 text-white shadow-sm" : "text-slate-500 hover:bg-white/70 hover:text-slate-800"
                  }`}
                >
                  <type.Icon size={15} className={active ? "text-white" : "text-slate-400"} />
                  {type.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[1.65fr_0.85fr]">
            <div className="space-y-6">
              {/* Booking details */}
              <Card title="Booking Details" description="Customer, reference and status.">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="Customer *">
                    <select
                      className={INPUT_CLASS}
                      value={form.customerId}
                      onChange={(e) => {
                        const id = e.target.value;
                        const customer = customers.find((c) => String(c._id) === String(id));
                        setForm((prev) => ({
                          ...prev,
                          customerId: id,
                          // Auto-fill Sender from the customer's own record —
                          // still editable below if this booking needs
                          // different sender details.
                          senderName: customer?.contactName || prev.senderName,
                          senderCompany: customer?.companyName || customer?.tradingName || prev.senderCompany,
                          senderPhone: customer?.phone || prev.senderPhone,
                          senderEmail: customer?.email || prev.senderEmail,
                          senderAddress: customer
                            ? [customer.address?.line1, customer.address?.line2, customer.address?.city, customer.address?.region, customer.address?.postcode]
                                .filter(Boolean).join(", ")
                            : prev.senderAddress,
                        }));
                      }}
                    >
                      <option value="">Select customer</option>
                      <option value="__oneoff__">+ One-off / casual customer</option>
                      {customers.map((customer) => (
                        <option key={customer._id} value={customer._id}>
                          {customer.companyName || customer.tradingName || customer.contactName || customer.name || "Unnamed Customer"}
                        </option>
                      ))}
                    </select>
                  </Field>

                  {isOneOff ? (
                    <Field label="Customer / Company Name *">
                      <input
                        className={INPUT_CLASS}
                        value={form.oneOffName}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({
                            ...prev,
                            oneOffName: v,
                            senderCompany: prev.senderCompany === prev.oneOffName ? v : prev.senderCompany,
                          }));
                        }}
                        placeholder="Who is shipping? e.g. John Smith"
                      />
                    </Field>
                  ) : (
                    <Field label="Booking Reference">
                      <input
                        className={INPUT_CLASS}
                        value={form.reference}
                        onChange={(e) => updateField("reference", e.target.value)}
                        placeholder="Auto-generated if left blank"
                      />
                    </Field>
                  )}

                  {isOneOff && (
                    <Field label="Booking Reference">
                      <input
                        className={INPUT_CLASS}
                        value={form.reference}
                        onChange={(e) => updateField("reference", e.target.value)}
                        placeholder="Auto-generated if left blank"
                      />
                    </Field>
                  )}

                  <Field label="Status">
                    <select className={INPUT_CLASS} value={form.status} onChange={(e) => updateField("status", e.target.value)}>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="invoiced">Invoiced</option>
                      <option value="completed">Completed</option>
                    </select>
                  </Field>
                </div>

                {form.status === "confirmed" && (
                  <div className="mt-5 space-y-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
                    <p className="text-sm font-semibold text-slate-900">Owner's Risk Statement required to confirm</p>
                    <blockquote className="border-l-2 border-amber-300 py-1 pl-4 text-sm italic leading-relaxed text-slate-700">
                      "These goods are to be carried at owner's risk. This means that the carrier will not pay
                      compensation if the goods are lost or damaged unless the carrier intentionally loses or damages them."
                    </blockquote>
                    <p className="text-xs text-slate-500">
                      For phone/manual bookings, confirm the customer has read and agreed to this before ticking below.
                    </p>
                    <Field label="Customer's Full Name">
                      <input
                        type="text"
                        className={INPUT_CLASS}
                        value={ownersRiskName}
                        onChange={(e) => setOwnersRiskName(e.target.value)}
                        placeholder="Type the customer's full name"
                      />
                    </Field>
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        checked={ownersRiskExecuted}
                        onChange={(e) => setOwnersRiskExecuted(e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-slate-950 focus:ring-slate-950"
                      />
                      <span className="text-sm text-slate-700">Customer has read and agreed to the Owner's Risk Statement above.</span>
                    </label>
                  </div>
                )}
              </Card>

              {/* Sailing & route */}
              <Card title="Sailing & Route" description="Only Open or Draft sailings can be booked onto.">
                <Field label="Requested Sailing *">
                  <select className={INPUT_CLASS} value={form.sailingId} onChange={(e) => updateField("sailingId", e.target.value)}>
                    <option value="">Select sailing</option>
                    {bookableSailings.map((sailing) => (
                      <option key={sailing._id} value={sailing._id}>{getSailingLabel(sailing)}</option>
                    ))}
                  </select>
                  {bookableSailings.length === 0 && (
                    <p className="mt-1.5 text-xs text-amber-600">No Open or Draft sailings available to book onto right now.</p>
                  )}
                </Field>

                {selectedSailing && (
                  <div className="mt-5">
                    <div className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-4">
                      <span className="shrink-0 text-sm font-bold text-slate-900">
                        {selectedSailing.origin || selectedSailing.departurePort || "-"}
                      </span>
                      <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-950" />
                      <div className="flex-1 border-t-2 border-dashed border-slate-300" />
                      <Anchor size={14} className="shrink-0 text-slate-400" />
                      <div className="flex-1 border-t-2 border-dashed border-slate-300" />
                      <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-950" />
                      <span className="shrink-0 text-sm font-bold text-slate-900">
                        {selectedSailing.destination || selectedSailing.arrivalPort || "-"}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                      <InfoTile label="Vessel" value={selectedSailing.vessel?.name || selectedSailing.vesselName || "TBC"} />
                      <InfoTile label="Booking Cut-off" value={cutoff ? formatDate(cutoff) : "—"} />
                      <InfoTile label="Departs (ETD)" value={formatDate(selectedSailing.etd)} />
                      <InfoTile label="Arrives (ETA)" value={formatDate(selectedSailing.eta)} />
                    </div>
                  </div>
                )}
              </Card>

              {/* Sender */}
              <Card title="Sender" description="Who is consigning the goods or stock.">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="Name">
                    <input className={INPUT_CLASS} value={form.senderName} onChange={(e) => updateField("senderName", e.target.value)} placeholder="Full name" />
                  </Field>
                  <Field label="Company / Farm Name">
                    <input className={INPUT_CLASS} value={form.senderCompany} onChange={(e) => updateField("senderCompany", e.target.value)} placeholder="Smith Farm Ltd" />
                  </Field>
                  <Field label="Phone">
                    <input className={INPUT_CLASS} type="tel" value={form.senderPhone} onChange={(e) => updateField("senderPhone", e.target.value)} />
                  </Field>
                  <Field label="Email">
                    <input className={INPUT_CLASS} type="email" value={form.senderEmail} onChange={(e) => updateField("senderEmail", e.target.value)} />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Address">
                      <input className={INPUT_CLASS} value={form.senderAddress} onChange={(e) => updateField("senderAddress", e.target.value)} placeholder="Farm / business address" />
                    </Field>
                  </div>
                </div>
              </Card>

              {/* Receiver */}
              <Card title="Receiver" description="Who is receiving at the destination.">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="Name">
                    <input className={INPUT_CLASS} value={form.receiverName} onChange={(e) => updateField("receiverName", e.target.value)} placeholder="Full name" />
                  </Field>
                  <Field label="Business Name">
                    <input className={INPUT_CLASS} value={form.receiverCompany} onChange={(e) => updateField("receiverCompany", e.target.value)} placeholder="Company or farm name" />
                  </Field>
                  <Field label="Phone">
                    <input className={INPUT_CLASS} type="tel" value={form.receiverPhone} onChange={(e) => updateField("receiverPhone", e.target.value)} />
                  </Field>
                  <Field label="Email">
                    <input className={INPUT_CLASS} type="email" value={form.receiverEmail} onChange={(e) => updateField("receiverEmail", e.target.value)} />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Delivery Address">
                      <input className={INPUT_CLASS} value={form.deliveryAddress} onChange={(e) => updateField("deliveryAddress", e.target.value)} placeholder="Street address, city" />
                    </Field>
                  </div>
                </div>
              </Card>

              {/* Agent + Emergency side by side */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Card title="Agent" description="Optional — acting on the sender's behalf.">
                  <div className="space-y-4">
                    <Field label="Name">
                      <input className={INPUT_CLASS} value={form.agentName} onChange={(e) => updateField("agentName", e.target.value)} placeholder="Full name" />
                    </Field>
                    <Field label="Phone">
                      <input className={INPUT_CLASS} type="tel" value={form.agentPhone} onChange={(e) => updateField("agentPhone", e.target.value)} />
                    </Field>
                    <Field label="Email">
                      <input className={INPUT_CLASS} type="email" value={form.agentEmail} onChange={(e) => updateField("agentEmail", e.target.value)} />
                    </Field>
                  </div>
                </Card>

                <Card title="Emergency Contact" description="Reachable during the voyage.">
                  <div className="space-y-4">
                    <Field label="Contact Name">
                      <input className={INPUT_CLASS} value={form.emergencyContact} onChange={(e) => updateField("emergencyContact", e.target.value)} placeholder="Full name" />
                    </Field>
                    <Field label="Phone">
                      <input className={INPUT_CLASS} type="tel" value={form.emergencyPhone} onChange={(e) => updateField("emergencyPhone", e.target.value)} />
                    </Field>
                    <Field label="Vet Emergency Phone">
                      <input className={INPUT_CLASS} type="tel" value={form.vetEmergencyPhone} onChange={(e) => updateField("vetEmergencyPhone", e.target.value)} />
                    </Field>
                  </div>
                </Card>
              </div>

              {/* Livestock */}
              {showLivestock && (
                <Card title="Animal Details" description="One group per species / class / farmer. Add as many as needed.">
                  <div className="space-y-4">
                    {livestockItems.map((item, idx) => (
                      <div key={idx} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <div className="mb-4 flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-700">Group {idx + 1}</span>
                          {livestockItems.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setLivestockItems((prev) => prev.filter((_, i) => i !== idx))}
                              className="text-xs font-semibold text-red-500 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                          <Field label="Animal Type">
                            <select className={INPUT_CLASS} value={item.livestockType} onChange={(e) => updateLivestock(idx, "livestockType", e.target.value)}>
                              <option value="cattle">Cattle</option>
                              <option value="sheep">Sheep</option>
                              <option value="deer">Deer</option>
                              <option value="other">Other</option>
                            </select>
                          </Field>
                          <Field label="Class / Status">
                            <select className={INPUT_CLASS} value={item.animalClass} onChange={(e) => updateLivestock(idx, "animalClass", e.target.value)}>
                              <option value="">— Select class —</option>
                              <option value="bull">Bull</option>
                              <option value="cow">Cow</option>
                              <option value="heifer">Heifer</option>
                              <option value="steer">Steer</option>
                              <option value="weaner">Weaner</option>
                              <option value="calf">Calf</option>
                              <option value="ram">Ram</option>
                              <option value="ewe">Ewe</option>
                              <option value="lamb">Lamb</option>
                              <option value="stag">Stag</option>
                              <option value="hind">Hind</option>
                              <option value="fawn">Fawn</option>
                              <option value="mixed">Mixed</option>
                              <option value="other">Other</option>
                            </select>
                          </Field>
                          <Field label="Head Count *">
                            <input className={INPUT_CLASS} type="number" min="0" value={item.headCount} onChange={(e) => updateLivestock(idx, "headCount", e.target.value)} placeholder="0" />
                          </Field>
                          <Field label="Average Weight (kg)">
                            <input className={INPUT_CLASS} type="number" min="0" value={item.averageWeightKg} onChange={(e) => updateLivestock(idx, "averageWeightKg", e.target.value)} placeholder="e.g. 450" />
                          </Field>
                          <Field label="Last Fed">
                            <DateTimePicker value={item.lastFed} onChange={(val) => updateLivestock(idx, "lastFed", val)} placeholder="Select date & time" />
                          </Field>
                          <Field label="Last Watered">
                            <DateTimePicker value={item.lastWatered} onChange={(val) => updateLivestock(idx, "lastWatered", val)} placeholder="Select date & time" />
                          </Field>
                        </div>

                        <div className="mt-4 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm">
                          <span className="font-semibold uppercase tracking-wide text-slate-500" style={{ fontSize: 11 }}>Est. total weight</span>
                          <span className="font-bold text-slate-900">
                            {((Number(item.headCount || 0) * Number(item.averageWeightKg || 0)) / 1000).toFixed(1)} t
                          </span>
                        </div>

                        <div className="mt-4 space-y-2">
                          <DeclarationCheck
                            checked={item.fitForTransport}
                            onChange={(v) => updateLivestock(idx, "fitForTransport", v)}
                            title="Fit for Transport Declaration"
                            desc="All animals in this group are fit for transport per MPI animal welfare requirements."
                          />
                          <DeclarationCheck
                            checked={item.vetCertificateUploaded}
                            onChange={(v) => updateLivestock(idx, "vetCertificateUploaded", v)}
                            title="Vet Certificate Provided"
                            desc="A valid veterinary certificate has been provided for this group."
                          />
                        </div>

                        <div className="mt-4">
                          <Field label="Notes">
                            <input className={INPUT_CLASS} value={item.notes} onChange={(e) => updateLivestock(idx, "notes", e.target.value)} placeholder="Condition, handling requirements..." />
                          </Field>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => setLivestockItems((prev) => [...prev, emptyLivestockItem()])}
                      className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      + Add Another Animal Group
                    </button>
                  </div>
                </Card>
              )}

              {/* Freight */}
              {showFreight && (
                <Card title="Freight Details" description="Add one or more freight items for this booking.">
                  <div className="space-y-4">
                    {freightItems.map((item, idx) => (
                      <div key={idx} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <div className="mb-4 flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-700">Item {idx + 1}</span>
                          {freightItems.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setFreightItems((prev) => prev.filter((_, i) => i !== idx))}
                              className="text-xs font-semibold text-red-500 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                          <Field label="Type">
                            <select className={INPUT_CLASS} value={item.freightType} onChange={(e) => updateFreight(idx, "freightType", e.target.value)}>
                              <option value="pallet">Pallet</option>
                              <option value="container">Container</option>
                              <option value="vehicle">Vehicle / RoRo</option>
                              <option value="looseItem">Loose Item</option>
                              <option value="bulk">Bulk</option>
                              {form.bookingType === "dg" && <option value="dangerous_goods">Dangerous Goods</option>}
                            </select>
                          </Field>
                          <Field label="Quantity">
                            <input className={INPUT_CLASS} type="number" min="0" value={item.quantity} onChange={(e) => updateFreight(idx, "quantity", e.target.value)} placeholder="0" />
                          </Field>
                          <Field label="Description">
                            <input className={INPUT_CLASS} value={item.description} onChange={(e) => updateFreight(idx, "description", e.target.value)} placeholder="e.g. Farm supplies" />
                          </Field>
                        </div>

                        <div className="mt-4 border-t border-slate-200 pt-4">
                          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Weight &amp; Dimensions</p>
                          <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
                            <Field label="Weight (kg)">
                              <input className={INPUT_CLASS} type="number" min="0" value={item.weightKg} onChange={(e) => updateFreight(idx, "weightKg", e.target.value)} placeholder="0" />
                            </Field>
                            <Field label="Length per item (m)">
                              <input className={INPUT_CLASS} type="number" min="0" step="0.01" value={item.lengthM} onChange={(e) => updateFreight(idx, "lengthM", e.target.value)} placeholder="e.g. 12" />
                            </Field>
                            <Field label="Width (m)">
                              <input className={INPUT_CLASS} type="number" min="0" step="0.01" value={item.widthM} onChange={(e) => updateFreight(idx, "widthM", e.target.value)} placeholder="0" />
                            </Field>
                            <Field label="Height (m)">
                              <input className={INPUT_CLASS} type="number" min="0" step="0.01" value={item.heightM} onChange={(e) => updateFreight(idx, "heightM", e.target.value)} placeholder="0" />
                            </Field>
                            <Field label="Cube (m³)">
                              <input className={INPUT_CLASS} type="number" min="0" step="0.001" value={item.cubeM3} onChange={(e) => updateFreight(idx, "cubeM3", e.target.value)} placeholder="0" />
                            </Field>
                            <Field label="Linear metres">
                              <div className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3.5 py-2.5 text-sm font-semibold text-slate-700">
                                {(Number(item.lengthM || 0) * Math.max(Number(item.quantity || 0), 1)).toLocaleString(undefined, { maximumFractionDigits: 2 })} m
                              </div>
                            </Field>
                          </div>
                          <p className="mt-2 text-xs text-slate-400">
                            Cube fills automatically from quantity × L × W × H — overwrite it if you have an exact figure. Linear metres = length per item × quantity, and is what vehicles are charged on.
                          </p>
                        </div>

                        <div className="mt-4">
                          <Field label="Notes">
                            <input className={INPUT_CLASS} value={item.notes} onChange={(e) => updateFreight(idx, "notes", e.target.value)} placeholder="Handling requirements..." />
                          </Field>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => setFreightItems((prev) => [...prev, emptyFreightItem()])}
                      className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      + Add Another Item
                    </button>
                  </div>
                </Card>
              )}

              {/* Machinery */}
              {showMachinery && (
                <Card title="Machinery / Vehicle Details" description="Equipment, vehicles and plant for Ro-Ro or crane lift.">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="md:col-span-3">
                      <Field label="Description *">
                        <input className={INPUT_CLASS} value={machinery.description} onChange={(e) => setMachinery((p) => ({ ...p, description: e.target.value }))} placeholder="e.g. John Deere tractor, 2019 Toyota Hilux" />
                      </Field>
                    </div>
                    <Field label="Make"><input className={INPUT_CLASS} value={machinery.make} onChange={(e) => setMachinery((p) => ({ ...p, make: e.target.value }))} placeholder="e.g. John Deere" /></Field>
                    <Field label="Model"><input className={INPUT_CLASS} value={machinery.model} onChange={(e) => setMachinery((p) => ({ ...p, model: e.target.value }))} placeholder="e.g. 6120M" /></Field>
                    <Field label="Weight (kg)"><input className={INPUT_CLASS} type="number" value={machinery.weightKg} onChange={(e) => setMachinery((p) => ({ ...p, weightKg: e.target.value }))} placeholder="0" /></Field>
                    <Field label="Length / Linear metres (m) *"><input className={INPUT_CLASS} type="number" min="0" step="0.01" value={machinery.lengthM} onChange={(e) => setMachinery((p) => ({ ...p, lengthM: e.target.value }))} placeholder="e.g. 12.5" /></Field>
                    <Field label="Width (m)"><input className={INPUT_CLASS} type="number" step="0.1" value={machinery.widthM} onChange={(e) => setMachinery((p) => ({ ...p, widthM: e.target.value }))} placeholder="0.0" /></Field>
                    <Field label="Height (m)"><input className={INPUT_CLASS} type="number" step="0.1" value={machinery.heightM} onChange={(e) => setMachinery((p) => ({ ...p, heightM: e.target.value }))} placeholder="0.0" /></Field>
                  </div>

                  <p className="mt-3 text-xs text-slate-500">
                    Vehicles and machinery are charged per linear metre — the length entered above is what is priced.
                  </p>

                  <div className="mt-5 space-y-3">
                    <label className="flex cursor-pointer items-center gap-3">
                      <input type="checkbox" checked={machinery.roRo} onChange={(e) => setMachinery((p) => ({ ...p, roRo: e.target.checked }))} className="h-4 w-4 rounded border-slate-300" />
                      <div>
                        <div className="text-sm font-semibold text-slate-800">Ro-Ro capable (self-propelled or towable)</div>
                        <div className="text-xs text-slate-500">Can be driven or towed aboard — untick if crane lift required</div>
                      </div>
                    </label>
                    <label className="flex cursor-pointer items-center gap-3">
                      <input type="checkbox" checked={machinery.hazardous} onChange={(e) => setMachinery((p) => ({ ...p, hazardous: e.target.checked }))} className="h-4 w-4 rounded border-slate-300" />
                      <div>
                        <div className="text-sm font-semibold text-slate-800">Contains hazardous materials</div>
                        <div className="text-xs text-slate-500">Fuel tanks, batteries, chemicals</div>
                      </div>
                    </label>
                  </div>

                  <div className="mt-5">
                    <Field label="Additional Notes">
                      <textarea className={`${INPUT_CLASS} resize-none`} rows={3} value={machinery.notes} onChange={(e) => setMachinery((p) => ({ ...p, notes: e.target.value }))} placeholder="Special handling, must be steam-cleaned, etc." />
                    </Field>
                  </div>

                  <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    <strong>Note:</strong> Used vehicles and equipment must be thoroughly steam-cleaned and free of dirt and seeds before loading (biosecurity requirement for Chatham Islands shipments).
                  </div>
                </Card>
              )}

              {/* Documents */}
              <Card title="Documents & Certificates" description="Vet certificates, NAIT documents, MSDS sheets, dangerous goods declarations or other supporting documents.">
                <label className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 transition ${uploading ? "cursor-wait border-slate-200 bg-slate-50" : "cursor-pointer border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100"}`}>
                  <Paperclip size={28} className="text-slate-400" />
                  <div className="text-center">
                    <div className="text-sm font-semibold text-slate-700">{uploading ? "Uploading..." : "Click to upload files"}</div>
                    <div className="mt-1 text-xs text-slate-400">PDF, images (JPG, PNG), Excel, Word — multiple files supported</div>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.doc,.docx"
                    className="hidden"
                    disabled={uploading}
                    onChange={handleFileUpload}
                  />
                </label>

                {uploadedDocs.length > 0 && (
                  <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                    <div className="grid grid-cols-[1fr_150px_90px_40px] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      <div>File</div><div>Category</div><div>Size</div><div />
                    </div>
                    {uploadedDocs.map((doc, i) => (
                      <div key={i} className={`grid grid-cols-[1fr_150px_90px_40px] items-center gap-3 px-4 py-3 text-sm ${i > 0 ? "border-t border-slate-100" : ""}`}>
                        <div className="flex min-w-0 items-center gap-2">
                          <FileText size={16} className="shrink-0 text-slate-400" />
                          <div className="min-w-0">
                            <div className="truncate font-medium text-slate-900">{doc.name}</div>
                            {doc.url && (
                              <a href={doc.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">View</a>
                            )}
                          </div>
                        </div>
                        <select
                          value={doc.category || "supporting_document"}
                          onChange={(e) => setUploadedDocs((prev) => prev.map((d, j) => (j === i ? { ...d, category: e.target.value } : d)))}
                          className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700 outline-none"
                        >
                          <option value="vet_certificate">Vet Certificate</option>
                          <option value="nait">NAIT Document</option>
                          <option value="msds">MSDS</option>
                          <option value="supporting_document">Supporting Document</option>
                        </select>
                        <div className="text-xs text-slate-400">{formatFileSize(doc.size)}</div>
                        <button
                          type="button"
                          onClick={() => setUploadedDocs((prev) => prev.filter((_, j) => j !== i))}
                          className="text-lg leading-none text-slate-300 transition hover:text-red-500"
                          aria-label="Remove file"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card title="Additional Notes">
                <textarea
                  className={`${INPUT_CLASS} resize-none`}
                  rows={4}
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Anything else we should know about this booking..."
                />
              </Card>

              {/* Bottom submit row (mirrors the public form) */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate(backTo)}
                  className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-slate-950 px-8 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {saving ? (isEditing ? "Saving..." : "Creating...") : (isEditing ? "Save Changes" : "Create Booking")}
                </button>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-6 xl:sticky xl:top-6">
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="px-6 py-5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Estimated Price</div>
                  <div className="mt-1 text-3xl font-bold text-slate-950">
                    ${Number(calculatedPricing || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {selectedCustomerName}
                    {" · "}
                    {isOneOff ? "Standard rates" : selectedCustomer?.rates?.rateSource || "Customer rates"}
                  </div>
                </div>

                <div className="space-y-3 border-t border-slate-100 px-6 py-4">
                  {pricingLines.map((line, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800">{line.label}</div>
                        <div className="text-xs text-slate-500">{line.basis}</div>
                      </div>
                      <div className="shrink-0 font-semibold text-slate-900">
                        ${line.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  ))}

                  {!selectedCustomer && !isOneOff && (
                    <p className="text-xs text-slate-400">Select a customer to calculate pricing.</p>
                  )}
                  {(selectedCustomer || isOneOff) && pricingLines.length === 0 && (
                    <p className="text-xs text-slate-400">Add animal / freight details to see pricing.</p>
                  )}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-5">
                  <h2 className="text-lg font-bold text-slate-950">Live Capacity</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {selectedSailing ? "Confirmed bookings plus this one." : "Select a sailing to see capacity."}
                  </p>
                </div>
                <div className="space-y-4">
                  <CapacityBar label="Livestock" value={projectedUsage.livestockHeads} limit={CAPACITY_LIMITS.livestockHeads} unit=" hd" pct={pct(projectedUsage.livestockHeads, CAPACITY_LIMITS.livestockHeads)} />
                  <CapacityBar label="Weight" value={projectedUsage.weightKg} limit={CAPACITY_LIMITS.weightKg} unit=" kg" pct={pct(projectedUsage.weightKg, CAPACITY_LIMITS.weightKg)} />
                  <CapacityBar label="Cube" value={projectedUsage.freightCube} limit={CAPACITY_LIMITS.freightCube} unit=" m³" pct={pct(projectedUsage.freightCube, CAPACITY_LIMITS.freightCube)} />

                  {!capacityCheck.valid && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-medium text-red-700">
                      {capacityCheck.message}
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </form>
      </div>
    </div>
  );
}


function buildSailingSnapshot(sailing) {
  if (!sailing) return null;

  return {
    sailingMongoId: sailing._id || "",
    sailingRef: sailing.sailingRef || sailing.sailingId || "",
    vesselName: sailing.vessel?.name || sailing.vesselName || "",
    route:
      sailing.route ||
      sailing.routeLabel ||
      `${sailing.origin || sailing.departurePort || "-"} → ${
        sailing.destination || sailing.arrivalPort || "-"
      }`,
    etd: sailing.etd || "",
    eta: sailing.eta || "",
    status: sailing.status || "",
  };
}

function getSailingLabel(sailing) {
  const ref = sailing.sailingRef || sailing.sailingId || sailing._id;
  const route =
    sailing.route ||
    sailing.routeLabel ||
    `${sailing.origin || sailing.departurePort || "-"} → ${
      sailing.destination || sailing.arrivalPort || "-"
    }`;

  const etd = sailing.etd ? ` — ${formatDateShort(sailing.etd)}` : "";

  return `${ref} — ${route}${etd}`;
}


function Card({ title, description, children }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-5">
        <h2 className="text-lg font-bold text-slate-950">{title}</h2>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function InfoTile({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-semibold text-slate-900">{value || "—"}</p>
    </div>
  );
}

function DeclarationCheck({ checked, onChange, title, desc }) {
  return (
    <label className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3.5 transition ${checked ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-100"}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300"
      />
      <div>
        <div className={`text-sm font-semibold ${checked ? "text-emerald-800" : "text-slate-800"}`}>{title}</div>
        <div className={`mt-0.5 text-xs leading-relaxed ${checked ? "text-emerald-700" : "text-slate-500"}`}>{desc}</div>
      </div>
    </label>
  );
}

function CapacityBar({ label, value, limit, unit, pct }) {
  const tone = pct >= 100 ? "bg-red-500" : pct >= 85 ? "bg-amber-500" : "bg-slate-900";
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between text-sm">
        <span className="font-semibold text-slate-700">{label}</span>
        <span className="text-xs text-slate-500">
          <span className="font-semibold text-slate-900">{Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
          {" / "}{limit.toLocaleString()}{unit}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function toTitle(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDateShort(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}